import React, { useState, useEffect, useRef, useCallback } from 'react';
import _ from 'lodash';
import classnames from 'classnames/bind';
import Paginator from '../../SearchPaginator';
import style from './index.module.scss';
import filterProductByName from '../../../utils/filterProductByName';
import underline from '../../../utils/underline';
import instance from '../../../services/httpRequest';
import { ISearchProduct as IProduct, Replacement } from '../../../interface';
import Loading from '../../Loading';
import oilThumb from '../../../images/oil-thumb-placeholder.png';
import paginatorConfig from '../../../config/paginatorConfig';
import localesReplacement from '../../../utils/localesReplacement';
import DOMPurify from 'dompurify';

const cx = classnames.bind(style);

export interface IProps {
  replacements: Replacement;
  onProductSelect?: (value: string | number, product: string) => void;
  selectedWorkshop: boolean;
  restore?: boolean;
  preserved?: boolean;
}

export const OilSearch = (props: IProps) => {
  const { replacements, onProductSelect, selectedWorkshop, restore, preserved } =
    props;
  const ref = useRef<NodeJS.Timeout | null>(null);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [value, setValue] = useState<string>('');
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [mPageIndex, setMPageIndex] = useState<number>(0);
  const [isMount, setIsMount] = useState<boolean>(false);
  const [finish, setFinish] = useState<boolean>(false);
  const limit = paginatorConfig.LIMIT;
  const controller = new AbortController();

  const memoDate = useCallback(() => {
    selectedWorkshop
      ? _.assign(instance.defaults.headers, { isWorkshopOwner: selectedWorkshop })
      : _.unset(instance.defaults.headers, 'isWorkshopOwner');
    instance
      .get('/products/all', {
        signal: controller.signal,
      })
      .then((d) => {
        d && setProducts(d.data);
      }).catch(() => _.noop());
  }, [selectedWorkshop]);

  useEffect(() => {
    return () => {
      controller.abort();
    };
  }, []);
  const mobileProducts = _.take(
    filterProductByName(products, value),
    limit * (mPageIndex + 1),
  );
  const onChangeHandler = (value: string) => {
    setValue(value);
    restore && sessionStorage.setItem('searchOKey', value);
    if (value.length >= 3) {
      setIsMount(true);
      setFinish(false);
      ref.current && clearTimeout(ref.current);
      ref.current = setTimeout(() => {
        products && setIsMount(false);
        setFinish(true);
        const window: any = globalThis;

        window.dataLayer.push({
          'event': !_.isEmpty(filterProductByName(products, value)) ? 'oilSearches' : 'detailsUnavailable_oilSearches',
          'pageType': 'product finder',
          'searchQuery': value,
          'searchType': 'oil search',
          'userType': (sessionStorage.getItem('selectedType') === 'true') ? 'workshop owner' : 'vehicle owner',
          'countryCode': localStorage.getItem('countryCode')
        });
      }, 1000);
    } else {
      setFinish(false);
    }
  };
  const changeIndex = (index: number) => {
    setPageIndex(index);
  };
  const showMore = () => {
    setMPageIndex(mPageIndex + 1);
  };

  useEffect(() => {
    memoDate();
    setProducts([]);
    setValue(value);
  }, [selectedWorkshop]);

  useEffect(() => {
    if (preserved) {
      setValue(sessionStorage.getItem('searchOKey') ?? '');
      if (!_.isEmpty(products)) {
        setIsMount(true);
        setTimeout(() => {
          setIsMount(false);
        }, 1000);
      }
    }
  }, [preserved, products]);

  return (
    <>
      <div className={cx('search-input')}>
        <input
          type='text'
          value={value}
          onChange={(ev) => onChangeHandler(ev.target.value)}
          placeholder={localesReplacement(
            replacements,
            'Search by the oil name',
          )}
        />
        {!!value.length && (
          <i
            className={cx('remove')}
            onClick={() => {
              setValue('');
              restore && sessionStorage.removeItem('searchOKey');
            }}
          ></i>
        )}
        <i className={cx('voice')}></i>
      </div>
      {!isMount ? (
        <>
          {!_.isEmpty(filterProductByName(products, value)) ? (
            <div
              className={cx('result', {
                'with-paginator':
                  filterProductByName(products, value) &&
                  Math.ceil(
                    filterProductByName(products, value).length / limit,
                  ) > 1,
              })}
            >
              <div className={cx('result-heading')}>
                <ul>
                  <li>{localesReplacement(replacements, 'Picture')}</li>
                  <li>{localesReplacement(replacements, 'Oil name')}</li>
                  <li>{localesReplacement(replacements, 'Description')}</li>
                  <li></li>
                </ul>
              </div>
              {_.chain(filterProductByName(products, value))
                .chunk(limit)
                .get(pageIndex)
                .map(product => (
                  <div
                    key={product.id}
                    className={cx('result-row', 'desktop', 'product-trigger')}
                    onClick={() =>
                      onProductSelect && onProductSelect(product['id'], product['name'])
                    }
                  >
                    <ul>
                      <li>
                        <img
                          src={
                            typeof product['packartUrl'] === 'string'
                              ? product['packartUrl']
                              : product['packartUrl']?.[0]
                                ? product['packartUrl'][0]
                                : oilThumb
                          }
                        />
                      </li>
                      <li
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            underline(product['name'], value),
                          ),
                        }}
                      ></li>
                      <li
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(product['description']),
                        }}
                      ></li>
                      <li>
                        <span className={cx('expend')}></span>
                        <button className={cx('recommonended-btn')}>
                          {localesReplacement(
                            replacements,
                            'View product page',
                          )}
                        </button>
                      </li>
                    </ul>
                  </div>
                ))
                .value()}
              {_.map(mobileProducts, (product, index) => (
                <div
                  key={index}
                  className={cx('result-row', 'mobile', 'product-trigger')}
                  onClick={() =>
                    onProductSelect && onProductSelect(product['id'], product['name'])
                  }
                >
                  <ul>
                    <li>
                      <img
                        src={
                          product['packartUrl']?.[0] ? product['packartUrl'][0] : oilThumb
                        }
                      />
                    </li>
                    <li
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          underline(product['name'], value),
                        ),
                      }}
                    ></li>
                    <li>
                      <button className={cx('recommonended-btn')}>
                        {localesReplacement(replacements, 'View product page')}
                      </button>
                    </li>
                  </ul>
                </div>
              ))}
              {!!filterProductByName(products, value) &&
                mobileProducts.length <
                filterProductByName(products, value).length && (
                  <div className={cx('btn-show-more')}>
                    <span onClick={showMore}>
                      {localesReplacement(replacements, 'Show more')}
                    </span>
                  </div>
                )}
              {!!products && (
                <Paginator
                  index={pageIndex}
                  changeIndexHandler={changeIndex}
                  total={Math.ceil(
                    filterProductByName(products, value).length / limit,
                  )}
                />
              )}
            </div>
          ) : (
            finish &&
            _.trim(value).length >= 3 && (
              <div className='no-match'>
                <div className='no-found-text no-oil-text'>
                  <p>
                    {localesReplacement(replacements, 'No results found for')}
                    <strong>{_.trim(value)}</strong>
                  </p>
                </div>
              </div>
            )
          )}
        </>
      ) : (
        <Loading />
      )}
    </>
  );
};

export default OilSearch;
